import { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { ROUTES } from '@/config/routes';
import { useOnboardingSession } from '@/contexts/OnboardingContext';
import { PersonDetailsForm } from '../components/PersonDetailsForm';
import type { Step4Data, PersonExtendedDetails } from '@/types/onboarding';

/**
 * Onboarding Wizard - Step 4: Maternal Grandparents
 *
 * This step collects information about the user's maternal grandparents:
 * - Mother's Father (maternal grandfather)
 * - Mother's Mother (maternal grandmother)
 *
 * Features:
 * - Optional step - users can skip entirely
 * - Pre-populates from session.draft_data.step4 if available
 * - Both grandparents are optional (firstName and lastName only required if providing data)
 * - Skip functionality clears all data and sets skipped flag
 * - Auto-save on successful validation
 * - Back navigation to Step 3
 * - Completion navigation (temporary: dashboard, future: completion flow)
 * - Loading states during save
 * - Error handling with user-friendly messages
 *
 * Design follows Stitch HTML reference (body section only):
 * @see E:\Job\IT\50Studios\Fiels\Clickup_Projects\projects\family-tree\files\stitch\stitch\wizard_maternal_grandparents_refined\code.html
 *
 * IMPORTANT: Shell and header remain consistent with Steps 1-3. Only body layout
 * is inspired by Stitch reference (two-column card layout).
 *
 * Integration:
 * - Uses OnboardingContext for session state and saveStep function
 * - Uses PersonDetailsForm (reusable component)
 * - Matches existing Step3 patterns for consistency
 *
 * NOTE: This is the final data collection step of the onboarding wizard.
 * Upon completion, the user will be redirected to the success screen where
 * they can create their family tree.
 */
export function Step4() {
  const { t } = useTranslation('onboarding');
  const navigate = useNavigate();
  const {
    session,
    isLoadingSession,
    isSaving,
    saveError,
    saveStep,
  } = useOnboardingSession();

  // Form state - separate for mother's father and mother's mother
  const [mothersFatherData, setMothersFatherData] = useState<PersonExtendedDetails>({
    firstName: '',
    lastName: '',
    gender: 'male',
  });

  const [mothersMotherData, setMothersMotherData] = useState<PersonExtendedDetails>({
    firstName: '',
    lastName: '',
    gender: 'female',
  });

  // Validation state
  const [showErrors, setShowErrors] = useState(false);
  const [validationErrors, setValidationErrors] = useState<{
    mothersFather?: Partial<Record<keyof PersonExtendedDetails, string>>;
    mothersMother?: Partial<Record<keyof PersonExtendedDetails, string>>;
  }>({});

  // Pre-populate from session data
  useEffect(() => {
    if (session?.draft_data?.step4) {
      const { mothersFather, mothersMother } = session.draft_data.step4;

      if (mothersFather) {
        setMothersFatherData({
          ...mothersFather,
          gender: 'male', // Ensure gender is set
        });
      }

      if (mothersMother) {
        setMothersMotherData({
          ...mothersMother,
          gender: 'female', // Ensure gender is set
        });
      }
    }
  }, [session]);

  // Validation function - Grandparents fields are all optional
  const validateForm = useCallback((): boolean => {
    // No validation required - all fields are optional for grandparents
    setValidationErrors({});
    return true;
  }, []);

  // Auto-validate when data changes (but don't show errors until user tries to submit)
  useEffect(() => {
    if (showErrors) {
      validateForm();
    }
  }, [mothersFatherData, mothersMotherData, showErrors, validateForm]);

  // Handle back navigation
  const handleBack = () => {
    navigate(ROUTES.ONBOARDING_STEP_3);
  };

  // Handle skip - Save with skipped flag and navigate to success screen
  const handleSkip = async () => {
    try {
      // Prepare Step 4 data with skipped flag
      const step4Data: Step4Data = {
        skipped: true,
      };

      // Save to backend
      await saveStep(4, step4Data);

      // Success - navigate to success screen
      toast.success(t('step4.skipSuccess', 'Step skipped successfully'));

      // Navigate to success screen
      navigate(ROUTES.ONBOARDING_SUCCESS);
    } catch (error) {
      console.error('Failed to skip Step 4:', error);

      // Show error toast
      toast.error(
        saveError?.message || t('step4.errors.skipFailed', 'Failed to skip step. Please try again.')
      );
    }
  };

  // Handle complete/save
  const handleComplete = async () => {
    setShowErrors(true);

    if (!validateForm()) {
      toast.error(t('step4.errors.validationFailed', 'Please fill in all required fields or skip this step'));
      return;
    }

    try {
      // Check if user provided any grandparent data
      const hasMothersFatherData =
        mothersFatherData.firstName.trim() &&
        mothersFatherData.lastName.trim();

      const hasMothersMotherData =
        mothersMotherData.firstName.trim() &&
        mothersMotherData.lastName.trim();

      // Prepare Step 4 data
      const step4Data: Step4Data = {
        skipped: false,
        ...(hasMothersFatherData && {
          mothersFather: {
            ...mothersFatherData,
            gender: 'male',
          },
        }),
        ...(hasMothersMotherData && {
          mothersMother: {
            ...mothersMotherData,
            gender: 'female',
          },
        }),
      };

      // If no grandparent data provided, just skip
      if (!hasMothersFatherData && !hasMothersMotherData) {
        step4Data.skipped = true;
      }

      // Save to backend
      await saveStep(4, step4Data);

      // Success - navigate to success screen
      toast.success(t('step4.saveSuccess', 'Maternal grandparents information saved successfully'));

      // Navigate to success screen
      navigate(ROUTES.ONBOARDING_SUCCESS);
    } catch (error) {
      console.error('Failed to save Step 4:', error);

      // Show error toast
      toast.error(
        saveError?.message || t('step4.errors.saveFailed', 'Failed to save progress. Please try again.')
      );
    }
  };

  // Show loading state while session is loading
  if (isLoadingSession) {
    return (
      <main className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-slate-500">{t('common.loading', 'Loading...')}</p>
        </div>
      </main>
    );
  }

  return (
    <main className="flex-1 flex justify-center py-6 sm:py-8 md:py-10 px-4 sm:px-6 md:px-8">
      <div className="layout-content-container flex flex-col max-w-5xl flex-1 gap-5 sm:gap-6 md:gap-8">
        {/* Progress Section */}
        <div className="flex flex-col gap-3 sm:gap-4">
          <div className="flex items-start sm:items-center justify-between gap-3 flex-wrap">
            <div className="flex flex-col">
              <span className="text-primary text-xs sm:text-sm font-bold uppercase tracking-wider">
                {t('step4.progress', 'Step 4 of 4')}
              </span>
              <h1 className="text-xl sm:text-2xl md:text-3xl font-bold mt-0.5 sm:mt-1 text-slate-900 dark:text-slate-100">
                {t('step4.title', 'Tell us about your maternal grandparents')}
              </h1>
            </div>
            <span className="text-slate-500 dark:text-slate-400 font-medium bg-slate-100 dark:bg-slate-800 px-2.5 sm:px-3 py-0.5 sm:py-1 rounded-full text-xs sm:text-sm flex-shrink-0">
              {t('step4.completionBadge', '3/4 Complete')}
            </span>
          </div>

          {/* Progress Bar */}
          <div className="w-full bg-slate-200 dark:bg-slate-700 h-2 sm:h-2.5 rounded-full overflow-hidden">
            <div
              className="bg-primary h-full transition-all duration-500"
              style={{ width: '75%' }}
            />
          </div>

          {/* Optional Subtitle */}
          <p className="text-slate-600 dark:text-slate-400 text-xs sm:text-sm">
            {t('step4.subtitle', 'This information is optional. You can skip this step if you don\'t have it.')}
          </p>
        </div>

        {/* Form Section - Two Column Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5 md:gap-6 lg:gap-8">
          {/* Mother's Father (Maternal Grandfather) */}
          <PersonDetailsForm
            title={t('step4.mothersFather', 'Mother\'s Father')}
            icon="man"
            initialData={mothersFatherData}
            onChange={setMothersFatherData}
            errors={validationErrors.mothersFather}
            showErrors={showErrors}
            requireNames={false}
          />

          {/* Mother's Mother (Maternal Grandmother) */}
          <PersonDetailsForm
            title={t('step4.mothersMother', 'Mother\'s Mother')}
            icon="woman"
            initialData={mothersMotherData}
            onChange={setMothersMotherData}
            errors={validationErrors.mothersMother}
            showErrors={showErrors}
            requireNames={false}
          />
        </div>

        {/* Footer Navigation */}
        <div className="flex items-center justify-between pt-4 sm:pt-5 md:pt-6 mt-2 sm:mt-3 md:mt-4 border-t border-slate-200 dark:border-slate-800 gap-3">
          {/* Back Button */}
          <Button
            onClick={handleBack}
            variant="ghost"
            size="lg"
            disabled={isSaving}
            className="px-4 sm:px-6 md:px-8 h-11 sm:h-12 md:h-14 flex items-center justify-center gap-1.5 sm:gap-2 text-slate-600 dark:text-slate-400 font-bold hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors text-sm sm:text-base"
          >
            <span className="material-symbols-outlined text-lg sm:text-xl">arrow_back</span>
            <span className="hidden sm:inline">{t('step4.back', 'Back')}</span>
          </Button>

          {/* Complete Button - Skip button temporarily hidden */}
          <div className="flex items-center gap-2 sm:gap-3">
            {/* Skip Button - Temporarily hidden */}
            {/* <Button
              onClick={handleSkip}
              variant="outline"
              size="lg"
              disabled={isSaving}
              className="px-8 h-14 border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-400 font-bold hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
            >
              {t('step4.skip', 'Skip for now')}
            </Button> */}

            {/* Complete Button */}
            <Button
              onClick={handleComplete}
              size="lg"
              disabled={isSaving}
              className="px-6 sm:px-8 md:px-10 h-11 sm:h-12 md:h-14 bg-primary text-white rounded-lg sm:rounded-xl font-bold shadow-lg shadow-primary/20 hover:brightness-110 active:scale-[0.98] transition-all text-sm sm:text-base"
            >
              {isSaving ? (
                <span className="flex items-center gap-2">
                  <span className="w-4 h-4 sm:w-5 sm:h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  <span className="hidden sm:inline">{t('step4.saving', 'Saving...')}</span>
                  <span className="sm:hidden">{t('step4.savingShort', 'Saving')}</span>
                </span>
              ) : (
                <>
                  <span className="hidden sm:inline">{t('step4.complete', 'Complete')}</span>
                  <span className="sm:hidden">{t('step4.completeShort', 'Done')}</span>
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </main>
  );
}
